import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Building2, LayoutDashboard } from "lucide-react";

const CompanyInsightsReports = () => {
  const navigate = useNavigate();

  return (
    <AppLayout>
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Back to Dashboard Button */}
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Header Section */}
        <div className="mb-8 p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-border">
          <h1 className="text-3xl font-bold text-foreground mb-4">Advanced Company Insights Report</h1>
          <p className="text-lg font-semibold text-primary mb-4">
            Stop being one of hundreds. Become the only logical choice.
          </p>
          
          <div className="space-y-4 text-muted-foreground">
            <p>
              This report is designed to position you not just as another qualified applicant like the hundreds of others seeking the job, but as the clear and only answer to the company's biggest pain points and most pressing needs—even the ones they may not have fully identified themselves. Armed with this intelligence, you can show that you understand their challenges in greater detail than they do, and you can present realistic, data-backed solutions that prove why you are the right hire.
            </p>
            <p>
              Every insight is drawn from a thorough check of multiple sources—including company websites, official career pages, LinkedIn activity, press releases, industry statistics, HR trend data, and leadership reports—so you can be confident the recommendations reflect what the company truly values and struggles with today.
            </p>

            <div className="mt-6">
              <h2 className="text-xl font-semibold text-foreground mb-3">Why Tailored Company Insights Matter</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong>Hiring managers scan resumes in under 10 seconds.</strong> One study shows they typically spend just 6–8 seconds reviewing each resume. Precision and relevance are critical.</li>
                <li><strong>Only 25% of resumes make it past Applicant Tracking Systems (ATS).</strong> Approximately 75% are rejected due to poor formatting or missing keywords. Tailoring ensures your application reaches a human.</li>
                <li><strong>Tailoring boosts callback rates significantly:</strong>
                  <ul className="ml-6 mt-1 space-y-1 list-disc list-inside">
                    <li>One case study showed a 40% increase in interview callbacks after customizing resumes and cover letters</li>
                    <li>Other data suggests personalized resumes can improve interview success by up to 50%</li>
                  </ul>
                </li>
                <li><strong>Recruiters identify lack of tailoring as a major issue.</strong> 63% of recruiters say the biggest mistake job seekers make is not customizing their resumes for each role.</li>
                <li><strong>A targeted skills section also makes a difference.</strong> Only 51% of candidates include relevant keywords and skills on their resumes—even though this is essential to pass ATS and grab recruiter attention.</li>
              </ul>
            </div>

            <p className="text-lg font-semibold text-primary mt-4">
              Know their problems. Align with their goals. Be the solution.
            </p>
          </div>
        </div>

        {/* Navigation and Actions */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/company-insights")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Company Insights
            </Button>
          </div>
          <Button onClick={() => navigate("/company-insights")}>
            <Building2 className="h-4 w-4 mr-2" />
            New Research
          </Button>
        </div>

        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Coming Soon</h3>
            <p className="text-muted-foreground mb-4 text-center">Company Insights reports will appear here once the feature is fully enabled.</p>
            <Button onClick={() => navigate("/company-insights")}>Research a Company</Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default CompanyInsightsReports;
